import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Download, FileText, RefreshCcw } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import StatusSemanticNote from '@/components/StatusSemanticNote';
import { API_BASE } from '@/lib/utils';
import {
  datetimeLocalToIso,
  formatInTimezone,
  timezoneLabel,
  useWorkspaceTimezone,
} from '@/lib/timezone';

// const API_BASE = typeof window !== "undefined" ? "" : "http://localhost:8001";

const SECTION_TABS = [
  ['executive_overview', 'Overview'],
  ['attendee_registrations', 'Registrations'],
  ['registration_revenue_funnel', 'Funnel'],
  ['ticket_engine_performance', 'Ticketing'],
  ['live_checkin_analytics', 'Check-in'],
  ['attendee_lifecycle_geography', 'Audience'],
  ['session_demand_capacity', 'Sessions'],
  ['communications_performance', 'Comms'],
  ['wallet_distribution', 'Wallet'],
  ['finance_refund_snapshot', 'Finance'],
  ['data_contract_quality', 'Data contracts'],
];

export default function Reports() {
  const { id } = useParams();
  const displayTimezone = useWorkspaceTimezone();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [catalog, setCatalog] = useState(null);
  const [activeTab, setActiveTab] = useState('executive_overview');
  const [refreshing, setRefreshing] = useState(false);

  const loadCatalog = async (opts = { silent: false }) => {
    if (!opts.silent) setLoading(true);
    else setRefreshing(true);
    setLoadError('');
    try {
      const res = await fetch(`${API_BASE}/api/events/${id}/reports/catalog`);
      if (!res.ok) throw new Error('Failed to load report catalog');
      const payload = await res.json();
      setCatalog(payload);
    } catch (err) {
      setCatalog(null);
      setLoadError(String(err?.message || 'unknown error'));
      toast.error('Unable to load reports', { description: String(err?.message || 'unknown error') });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadCatalog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const sectionMap = useMemo(() => {
    const rows = catalog?.sections || [];
    const map = {};
    rows.forEach((sec) => {
      if (sec?.id) map[sec.id] = sec;
    });
    return map;
  }, [catalog]);

  const eventMeta = catalog?.event || { title: 'Event' };

  const exportDataset = (dataset) => {
    const url = `${API_BASE}/api/events/${id}/reports/export.csv?dataset=${encodeURIComponent(dataset)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    toast.success(`Export started: ${dataset}`);
  };

  const renderCards = (cards = []) => (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-[var(--border-default)] border border-[var(--border-default)]">
      {(Array.isArray(cards) ? cards : []).map((c, idx) => (
        <div key={`${c?.label || 'card'}-${idx}`} className="bg-[var(--bg-surface)] p-6">
          <div className="text-overline">{c?.label || 'Metric'}</div>
          <div className="font-mono text-3xl mt-2">
            {c?.prefix || ''}
            {typeof c?.value === 'number' ? c.value.toLocaleString() : (c?.value ?? '—')}
            {c?.suffix || ''}
          </div>
        </div>
      ))}
    </div>
  );

  const stalenessLabel = useMemo(() => {
    const s = Number(catalog?.stalenessSeconds || 0);
    if (s < 60) return `${s}s behind`;
    if (s < 3600) return `${Math.floor(s / 60)}m behind`;
    return `${Math.floor(s / 3600)}h behind`;
  }, [catalog?.stalenessSeconds]);

  const freshnessState = useMemo(() => {
    const s = Number(catalog?.stalenessSeconds || 0);
    if (s <= 30) return { label: 'Live', className: 'text-[var(--primary)] border-[var(--primary)]/40 bg-[var(--primary)]/10' };
    if (s <= 300) return { label: 'Recent', className: 'text-[var(--text-secondary)] border-[var(--border-default)] bg-[var(--bg-elev)]' };
    return { label: 'Stale', className: 'text-[#E25D3A] border-[#E25D3A]/40 bg-[#E25D3A]/10' };
  }, [catalog?.stalenessSeconds]);

  return (
    <div
      className="h-full min-h-0 overflow-y-auto p-4 md:p-8 lg:p-12 max-w-[1500px] mx-auto"
      data-testid="reports-page"
    >
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-overline">{eventMeta.title} · reports</div>
          <h1 className="font-heading text-4xl mt-2 tracking-tight font-medium">Report hub</h1>
          <p className="text-xs font-mono text-[var(--text-tertiary)] mt-2 flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex items-center px-2 py-0.5 border font-mono text-[10px] uppercase tracking-widest ${freshnessState.className}`}
            >
              {freshnessState.label}
            </span>
            <span>
              Updated: {catalog?.updatedAt ? formatInTimezone(catalog.updatedAt, displayTimezone) : '—'} (
              {timezoneLabel(displayTimezone)})
            </span>
            <span>· Staleness: {stalenessLabel}</span>
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button className="btn-secondary" onClick={() => window.print()}>
            <FileText size={14} /> <span className="hidden sm:inline">Print</span>
          </button>
          <button className="btn-secondary" onClick={() => exportDataset('attendees')}>
            <Download size={14} /> <span className="hidden sm:inline">Attendees CSV</span>
          </button>
          <button className="btn-secondary" onClick={() => exportDataset('registrations')}>
            <Download size={14} /> <span className="hidden sm:inline">Registrations CSV</span>
          </button>
          <button className="btn-secondary" onClick={() => exportDataset('funnel')}>
            <Download size={14} /> <span className="hidden sm:inline">Funnel CSV</span>
          </button>
          <button className="btn-secondary" onClick={() => exportDataset('ticket_mix')}>
            <Download size={14} /> <span className="hidden sm:inline">Ticket mix CSV</span>
          </button>
          <button className="btn-secondary" onClick={() => exportDataset('data_contract_dq')}>
            <Download size={14} /> <span className="hidden sm:inline">DQ CSV</span>
          </button>
          <button className="btn-secondary" onClick={() => loadCatalog({ silent: true })}>
            <RefreshCcw size={14} /> {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {loading ? (
        <StatusSemanticNote
          state="loading"
          message="Loading report catalog. Export controls become actionable once data is loaded."
        />
      ) : null}

      {!loading && loadError ? (
        <StatusSemanticNote state="error" message={`Could not load reports: ${loadError}`} />
      ) : null}

      {!loading && catalog ? (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-10">
          <TabsList className="h-auto p-1 flex flex-wrap gap-1 bg-[var(--bg-surface)] border border-[var(--border-default)] rounded-none">
            {SECTION_TABS.map(([key, label]) => (
              <TabsTrigger
                key={key}
                value={key}
                className="rounded-none text-xs font-mono data-[state=active]:bg-[var(--primary)] data-[state=active]:text-[var(--on-primary)]"
              >
                {label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="executive_overview">
            {sectionMap.executive_overview ? renderCards(sectionMap.executive_overview.cards) : <EmptySection />}
          </TabsContent>

          <TabsContent value="attendee_registrations">
            <RegistrationsSection eventId={id} displayTimezone={displayTimezone} onExport={() => exportDataset('registrations')} />
          </TabsContent>

          <TabsContent value="registration_revenue_funnel">
            <FunnelSection section={sectionMap.registration_revenue_funnel} />
          </TabsContent>

          <TabsContent value="ticket_engine_performance">
            <TicketingSection section={sectionMap.ticket_engine_performance} />
          </TabsContent>

          <TabsContent value="live_checkin_analytics">
            <CheckinSection section={sectionMap.live_checkin_analytics} />
          </TabsContent>

          <TabsContent value="attendee_lifecycle_geography">
            <AudienceSection section={sectionMap.attendee_lifecycle_geography} />
          </TabsContent>

          <TabsContent value="session_demand_capacity">
            <SessionsSection section={sectionMap.session_demand_capacity} />
          </TabsContent>

          <TabsContent value="communications_performance">
            <CommunicationsSection section={sectionMap.communications_performance} />
          </TabsContent>

          <TabsContent value="wallet_distribution">
            <WalletSection section={sectionMap.wallet_distribution} />
          </TabsContent>

          <TabsContent value="finance_refund_snapshot">
            {sectionMap.finance_refund_snapshot ? (
              <div className="mt-6">
                {renderCards(sectionMap.finance_refund_snapshot.cards)}
                {sectionMap.finance_refund_snapshot.notes ? (
                  <div className="mt-4 text-xs text-[var(--text-tertiary)]">
                    {sectionMap.finance_refund_snapshot.notes}
                  </div>
                ) : null}
              </div>
            ) : (
              <EmptySection />
            )}
          </TabsContent>

          <TabsContent value="data_contract_quality">
            <DataContractSection section={sectionMap.data_contract_quality} />
          </TabsContent>
        </Tabs>
      ) : null}
      {!loading && catalog ? (
        <StatusSemanticNote
          state="readonly"
          message="Reports are read-only snapshots. Use module managers to edit source data."
        />
      ) : null}
    </div>
  );
}

function EmptySection() {
  return <div className="surface mt-6 p-6 text-sm text-[var(--text-tertiary)]">No data available.</div>;
}

function FunnelSection({ section }) {
  if (!section) return <EmptySection />;
  const funnel = Array.isArray(section.funnel) ? section.funnel : [];
  return (
    <div className="mt-6 space-y-6">
      <div className="surface p-6">
        <div className="space-y-3">
          {funnel.map((f, i) => {
            const baseline = funnel[0]?.value || 1;
            const pct = baseline > 0 ? (Number(f?.value || 0) / baseline) * 100 : 0;
            return (
              <div key={`${f?.label || 'step'}-${i}`} className="grid grid-cols-12 gap-4 items-center">
                <div className="col-span-4 text-sm text-[var(--text-secondary)]">{f?.label || 'Step'}</div>
                <div className="col-span-6 h-8 bg-[var(--bg-elev)]">
                  <div
                    className="h-full flex items-center px-3 font-mono text-xs text-[var(--on-primary)]"
                    style={{
                      width: `${pct}%`,
                      background: i === funnel.length - 1 ? 'var(--primary)' : 'rgba(217,242,94,0.75)',
                    }}
                  >
                    {Number(f.value || 0).toLocaleString()}
                  </div>
                </div>
                <div className="col-span-2 font-mono text-xs text-right">{pct.toFixed(1)}%</div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-[var(--border-default)] border border-[var(--border-default)]">
        {[
          ['Visits', section.metrics?.visits],
          ['Starts', section.metrics?.starts],
          ['Completes', section.metrics?.completes],
          ['Paid', section.metrics?.paid],
          ['Conversion', `${section.metrics?.conversionPct || 0}%`],
        ].map(([k, v]) => (
          <div key={k} className="bg-[var(--bg-surface)] p-4">
            <div className="text-overline">{k}</div>
            <div className="font-mono mt-1">{typeof v === 'number' ? v.toLocaleString() : v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TicketingSection({ section }) {
  if (!section) return <EmptySection />;
  return (
    <div className="surface mt-6 overflow-x-auto">
      <table className="w-full text-sm min-w-[500px]">
        <thead className="border-b border-[var(--border-default)] text-[var(--text-tertiary)] font-mono text-[10px] uppercase tracking-widest">
          <tr>
            <th className="p-4 text-left">Ticket</th>
            <th className="p-4 text-left">Type</th>
            <th className="p-4 text-right">Sold</th>
            <th className="p-4 text-right">Quota</th>
            <th className="p-4 text-right">Fill</th>
            <th className="p-4 text-right">Gross</th>
          </tr>
        </thead>
        <tbody>
          {(section.rows || []).map((r) => (
            <tr key={r.id} className="border-b border-[var(--border-default)]">
              <td className="p-4">{r.name}</td>
              <td className="p-4 text-xs font-mono text-[var(--text-tertiary)]">{r.type}</td>
              <td className="p-4 text-right font-mono">{r.sold}</td>
              <td className="p-4 text-right font-mono">{r.quota}</td>
              <td className="p-4 text-right font-mono">{r.fillPct}%</td>
              <td className="p-4 text-right font-mono">${Number(r.gross || 0).toLocaleString()}</td>
            </tr>
          ))}
          {(section.rows || []).length === 0 ? (
            <tr>
              <td className="p-6 text-[var(--text-tertiary)]" colSpan={6}>No ticket tiers configured for this event.</td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

function CheckinSection({ section }) {
  if (!section) return <EmptySection />;
  const series = section.hourly || [];
  const max = Math.max(...series.map((x) => x.count || 0), 1);
  return (
    <div className="mt-6 grid grid-cols-12 gap-6">
      <div className="col-span-12 lg:col-span-7 surface p-6">
        <div className="text-overline">Hourly check-ins</div>
        <svg viewBox="0 0 800 180" className="w-full h-[180px] mt-4">
          {series.map((point, i) => (
            <rect
              key={point.hour}
              x={(i / series.length) * 800}
              y={180 - ((point.count || 0) / max) * 160}
              width={Math.max(12, 800 / series.length - 4)}
              height={((point.count || 0) / max) * 160}
              fill="var(--primary)"
              opacity="0.85"
            />
          ))}
        </svg>
      </div>
      <div className="col-span-12 lg:col-span-5 surface p-6">
        <div className="text-overline">Gate split</div>
        <div className="mt-4 space-y-2">
          {(section.gateSplit || []).map((g) => (
            <div key={g.gate} className="flex justify-between text-sm">
              <span>{g.gate}</span>
              <span className="font-mono">{g.count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AudienceSection({ section }) {
  if (!section) return <EmptySection />;
  return (
    <div className="mt-6 grid grid-cols-12 gap-6">
      <SimpleTable title="Status lifecycle" rows={section.statusRows} leftKey="status" rightKey="count" />
      <SimpleTable title="Ticket split" rows={section.ticketRows} leftKey="ticket" rightKey="count" />
      <SimpleTable title="Top countries" rows={section.countryRows} leftKey="country" rightKey="count" />
    </div>
  );
}

function SessionsSection({ section }) {
  if (!section) return <EmptySection />;
  return (
    <div className="surface mt-6 overflow-x-auto">
      <table className="w-full text-sm min-w-[500px]">
        <thead className="border-b border-[var(--border-default)] text-[var(--text-tertiary)] font-mono text-[10px] uppercase tracking-widest">
          <tr>
            <th className="p-4 text-left">Session</th>
            <th className="p-4 text-left">Room</th>
            <th className="p-4 text-right">Registered</th>
            <th className="p-4 text-right">Session check-ins</th>
            <th className="p-4 text-right">Capacity</th>
            <th className="p-4 text-right">Fill</th>
          </tr>
        </thead>
        <tbody>
          {(section.rows || []).map((s) => (
            <tr key={s.id} className="border-b border-[var(--border-default)]">
              <td className="p-4">{s.title}</td>
              <td className="p-4 text-[var(--text-tertiary)]">{s.room}</td>
              <td className="p-4 text-right font-mono">{s.registered}</td>
              <td className="p-4 text-right font-mono">{Number(s.checkIns || 0).toLocaleString()}</td>
              <td className="p-4 text-right font-mono">{s.capacity}</td>
              <td className="p-4 text-right font-mono">{s.fillPct}%</td>
            </tr>
          ))}
          {(section.rows || []).length === 0 ? (
            <tr>
              <td className="p-6 text-[var(--text-tertiary)]" colSpan={6}>No sessions in schedule. Add sessions under Event → Schedule.</td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

function CommunicationsSection({ section }) {
  if (!section) return <EmptySection />;
  return (
    <div className="mt-6 space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[var(--border-default)] border border-[var(--border-default)]">
        {[
          ['Campaigns', section.summary?.campaignCount || 0],
          ['Sent', section.summary?.sent || 0],
          ['Avg open', `${section.summary?.avgOpenPct || 0}%`],
          ['Avg click', `${section.summary?.avgClickPct || 0}%`],
        ].map(([k, v]) => (
          <div key={k} className="bg-[var(--bg-surface)] p-4">
            <div className="text-overline">{k}</div>
            <div className="font-mono mt-1">{typeof v === 'number' ? v.toLocaleString() : v}</div>
          </div>
        ))}
      </div>
      <div className="surface overflow-x-auto">
        <table className="w-full text-sm min-w-[500px]">
          <thead className="border-b border-[var(--border-default)] text-[var(--text-tertiary)] font-mono text-[10px] uppercase tracking-widest">
            <tr>
              <th className="p-4 text-left">Campaign</th>
              <th className="p-4 text-right">Sent</th>
              <th className="p-4 text-right">Open</th>
              <th className="p-4 text-right">Click</th>
            </tr>
          </thead>
          <tbody>
            {(section.campaignRows || []).map((c) => (
              <tr key={c.id} className="border-b border-[var(--border-default)]">
                <td className="p-4">{c.name}</td>
                <td className="p-4 text-right font-mono">{Number(c.sent || 0).toLocaleString()}</td>
                <td className="p-4 text-right font-mono">{Math.round(Number(c.opens || 0) * 100)}%</td>
                <td className="p-4 text-right font-mono">{Math.round(Number(c.clicks || 0) * 100)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function WalletSection({ section }) {
  if (!section) return <EmptySection />;
  return (
    <div className="mt-6 space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[var(--border-default)] border border-[var(--border-default)]">
        {[
          ['Runs', section.summary?.runs || 0],
          ['Recipients', section.summary?.recipients || 0],
          ['Added', section.summary?.addedToWallet || 0],
          ['Adoption', `${section.summary?.adoptionPct || 0}%`],
        ].map(([k, v]) => (
          <div key={k} className="bg-[var(--bg-surface)] p-4">
            <div className="text-overline">{k}</div>
            <div className="font-mono mt-1">{typeof v === 'number' ? v.toLocaleString() : v}</div>
          </div>
        ))}
      </div>
      <div className="surface overflow-x-auto">
        <table className="w-full text-sm min-w-[500px]">
          <thead className="border-b border-[var(--border-default)] text-[var(--text-tertiary)] font-mono text-[10px] uppercase tracking-widest">
            <tr>
              <th className="p-4 text-left">Timestamp</th>
              <th className="p-4 text-left">Event</th>
              <th className="p-4 text-right">Recipients</th>
              <th className="p-4 text-right">Added</th>
            </tr>
          </thead>
          <tbody>
            {(section.distributionRows || []).map((row) => (
              <tr key={row.id || row.ts} className="border-b border-[var(--border-default)]">
                <td className="p-4 font-mono text-xs">{row.ts}</td>
                <td className="p-4">{row.event}</td>
                <td className="p-4 text-right font-mono">{Number(row.recipients || 0).toLocaleString()}</td>
                <td className="p-4 text-right font-mono">{Number(row.added || 0).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DataContractSection({ section }) {
  if (!section) return <EmptySection />;
  const bronze = section.bronze || {};
  const silver = section.silver || {};
  const gold = section.gold || {};
  const checks = Array.isArray(section.dqChecks) ? section.dqChecks : [];
  const ticketRows = Array.isArray(gold.ticketRevenueRows) ? gold.ticketRevenueRows : [];
  return (
    <div className="mt-6 space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[var(--border-default)] border border-[var(--border-default)]">
        <MetricCard label="Bronze attendees" value={Number(bronze.attendees_raw || 0).toLocaleString()} />
        <MetricCard label="Bronze purchases" value={Number(bronze.ticket_engine_purchases_raw || 0).toLocaleString()} />
        <MetricCard label="Silver rows" value={Number(silver.rowCount || 0).toLocaleString()} />
        <MetricCard label="Gold settled revenue" value={`$${Number(gold.settledRevenue || 0).toLocaleString()}`} />
      </div>

      <div className="surface p-6">
        <div className="text-overline mb-4">Data quality checks</div>
        <div className="space-y-2">
          {checks.map((check) => (
            <div
              key={check.id}
              className="flex items-center justify-between border-b border-dashed border-[var(--border-default)] pb-2 text-sm"
            >
              <div>
                <div className="text-[#F4F4F5]">{check.label}</div>
                <div className="text-xs text-[var(--text-tertiary)]">{check.detail}</div>
              </div>
              <div className="text-right">
                <div
                  className={`font-mono text-xs ${check.status === 'PASS' ? 'text-[var(--primary)]' : 'text-[var(--error)]'}`}
                >
                  {check.status}
                </div>
                <div className="font-mono text-xs text-[var(--text-secondary)]">{String(check.value ?? '—')}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="surface overflow-x-auto">
        <table className="w-full text-sm min-w-[500px]">
          <thead className="border-b border-[var(--border-default)] text-[var(--text-tertiary)] font-mono text-[10px] uppercase tracking-widest">
            <tr>
              <th className="p-4 text-left">Ticket</th>
              <th className="p-4 text-right">Settled orders</th>
              <th className="p-4 text-right">Settled revenue</th>
            </tr>
          </thead>
          <tbody>
            {ticketRows.map((row) => (
              <tr key={row.ticket} className="border-b border-[var(--border-default)]">
                <td className="p-4">{row.ticket}</td>
                <td className="p-4 text-right font-mono">{Number(row.settledOrders || 0).toLocaleString()}</td>
                <td className="p-4 text-right font-mono">${Number(row.settledRevenue || 0).toLocaleString()}</td>
              </tr>
            ))}
            {ticketRows.length === 0 ? (
              <tr>
                <td className="p-4 text-[var(--text-tertiary)]" colSpan={3}>
                  No settled ticket rows yet.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MetricCard({ label, value }) {
  return (
    <div className="bg-[var(--bg-surface)] p-4">
      <div className="text-overline">{label}</div>
      <div className="font-mono mt-1">{value}</div>
    </div>
  );
}

const REGISTRATION_STATUSES = ['', 'Registered', 'Invited', 'Pending Approval', 'Cancelled'];

function RegistrationsSection({ eventId, displayTimezone, onExport }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [registeredFrom, setRegisteredFrom] = useState('');
  const [registeredTo, setRegisteredTo] = useState('');
  const [registeredFromLocal, setRegisteredFromLocal] = useState('');
  const [registeredToLocal, setRegisteredToLocal] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '500', offset: '0' });
      if (statusFilter) params.set('status', statusFilter);
      if (registeredFrom) params.set('registered_from', registeredFrom);
      if (registeredTo) params.set('registered_to', registeredTo);
      const res = await fetch(
        `${API_BASE}/api/events/${eventId}/reports/registrations?${params.toString()}`,
      );
      if (!res.ok) throw new Error('Failed to load registrations');
      const payload = await res.json();
      setRows(Array.isArray(payload.rows) ? payload.rows : []);
      setTotal(Number(payload.total || 0));
    } catch (err) {
      toast.error('Unable to load registrations', { description: String(err?.message || 'unknown error') });
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (eventId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, statusFilter, registeredFrom, registeredTo]);

  const tzLabel = timezoneLabel(displayTimezone);

  return (
    <div className="mt-6 space-y-4" data-testid="registrations-report">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-overline">Attendee registrations</div>
          <p className="text-xs font-mono text-[var(--text-tertiary)] mt-1">
            {total.toLocaleString()} total · registration times in {tzLabel}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" className="btn-secondary" onClick={load}>
            <RefreshCcw size={14} /> Refresh
          </button>
          <button type="button" className="btn-secondary" onClick={onExport}>
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      <div className="surface p-4 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-4 items-end">
          <div className="min-w-0 sm:col-span-2 lg:col-span-4">
            <label className="text-overline block mb-2">Status</label>
            <select className="input-dark w-full" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">All statuses</option>
              {REGISTRATION_STATUSES.filter(Boolean).map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div className="min-w-0 sm:col-span-1 lg:col-span-3">
            <label className="text-overline block mb-2">Registered from</label>
            <input
              type="datetime-local"
              className="input-dark w-full min-w-0 font-mono text-xs"
              value={registeredFromLocal}
              onChange={(e) => {
                const local = e.target.value;
                setRegisteredFromLocal(local);
                setRegisteredFrom(local ? datetimeLocalToIso(local, displayTimezone) : '');
              }}
            />
          </div>
          <div className="min-w-0 sm:col-span-1 lg:col-span-3">
            <label className="text-overline block mb-2">Registered to</label>
            <input
              type="datetime-local"
              className="input-dark w-full min-w-0 font-mono text-xs"
              value={registeredToLocal}
              onChange={(e) => {
                const local = e.target.value;
                setRegisteredToLocal(local);
                setRegisteredTo(local ? datetimeLocalToIso(local, displayTimezone) : '');
              }}
            />
          </div>
          {registeredFromLocal || registeredToLocal || statusFilter ? (
            <div className="sm:col-span-2 lg:col-span-2 flex sm:justify-end">
              <button
                type="button"
                className="btn-ghost text-xs w-full sm:w-auto"
                onClick={() => {
                  setStatusFilter('');
                  setRegisteredFrom('');
                  setRegisteredTo('');
                  setRegisteredFromLocal('');
                  setRegisteredToLocal('');
                }}
              >
                Clear filters
              </button>
            </div>
          ) : null}
        </div>
        <p className="text-[10px] font-mono text-[var(--text-tertiary)]">
          Date filters use your workspace timezone ({tzLabel}).
        </p>
      </div>

      {loading ? (
        <StatusSemanticNote state="loading" message="Loading registration report…" />
      ) : (
        <div className="surface overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="border-b border-[var(--border-default)] text-[var(--text-tertiary)] font-mono text-[10px] uppercase tracking-widest">
              <tr>
                <th className="p-4 text-left">Name</th>
                <th className="p-4 text-left">Email</th>
                <th className="p-4 text-left">Company</th>
                <th className="p-4 text-left">Status</th>
                <th className="p-4 text-left">Ticket</th>
                <th className="p-4 text-left">Registered</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-[var(--border-default)]">
                  <td className="p-4">{r.name}</td>
                  <td className="p-4 font-mono text-xs">{r.email}</td>
                  <td className="p-4 text-[var(--text-secondary)]">{r.company || '—'}</td>
                  <td className="p-4 text-xs">{r.status}</td>
                  <td className="p-4 text-xs text-[var(--text-tertiary)]">{r.ticket || '—'}</td>
                  <td className="p-4 font-mono text-xs whitespace-nowrap" title={r.registeredAt || ''}>
                    <div>{formatInTimezone(r.registeredAt, displayTimezone, { second: '2-digit' })}</div>
                    {r.invitedAt ? (
                      <div className="text-[10px] text-[var(--text-tertiary)] mt-0.5">
                        Invited {formatInTimezone(r.invitedAt, displayTimezone, { second: '2-digit' })}
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
              {rows.length === 0 ? (
                <tr>
                  <td className="p-6 text-[var(--text-tertiary)]" colSpan={6}>
                    No registrations match the current filters.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
          {total > rows.length ? (
            <div className="p-4 text-xs font-mono text-[var(--text-tertiary)] border-t border-[var(--border-default)]">
              Showing {rows.length} of {total.toLocaleString()}. Narrow filters or export CSV for the full list.
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

function SimpleTable({ title, rows = [], leftKey, rightKey }) {
  return (
    <div className="col-span-12 lg:col-span-4 surface p-4">
      <div className="text-overline">{title}</div>
      <div className="mt-3 space-y-2">
        {rows.slice(0, 8).map((row, idx) => (
          <div key={`${row[leftKey]}-${idx}`} className="flex justify-between text-sm">
            <span className="text-[var(--text-secondary)] truncate pr-3">{row[leftKey]}</span>
            <span className="font-mono">{row[rightKey]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
